import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import CircularProgress from '@mui/material/CircularProgress';
import EChartsReact from 'echarts-for-react';

import { fetchOverAllScoreByCompany } from 'services/modules/cap/assessmentservice';

const CAPGraph = () => {
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

  const [loading, setLoading] = useState(false);
  const [overAllPercentage, setOverAllPercentage] = useState(0);
  const [series, setSeries] = useState(0);
  const [securityLevel, setSecurityLevel] = useState('Informational');
  const [securityColor, setSecurityColor] = useState('#008FFB');
  const [noData, setNoData] = useState(false);

  // Return emoji image based on overall percentage
  const getEmoji = (value) => {
    switch (true) {
      case value >= 80:
        return require("../../assets/emoji_1.png");
      case value >= 60:
        return require("../../assets/emoji_2.png");
      case value >= 40:
        return require("../../assets/emoji_3.png");
      case value >= 20:
        return require("../../assets/emoji_4.png");
      case value >= 0:
        return require("../../assets/emoji_5.png");
      default:
        return require("../../assets/emoji_1.png");
    }
  };

  // Gauge chart configuration
  const gaugeOptions = {
    tooltip: {
      responsive: true,
      position: "top",
      formatter: () => `${overAllPercentage * 10} Points`,
      padding: 5,
      transitionDuration: 0,
      hideDelay: 100,
      backgroundColor: "#fff",
      borderColor: "#c8e2f7",
      borderWidth: 0.2,
      textStyle: {
        color: "#414143",
        fontSize: 14,
        fontFamily: "Montserrat",
        fontWeight: 600,
      },
    },
    series: [
      {
        name: "Risk Posture",
        type: "gauge",
        startAngle: 200,
        endAngle: -20,
        min: 1,
        max: 10,
        splitNumber: 5,
        radius: "80%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 40,
            color: [
              [0.2, "#db3832"],
              [0.4, "#e56c39"],
              [0.6, "#fcdd50"],
              [0.8, "#9ac356"],
              [1, "#41914d"],
            ],
          },
        },
        pointer: {
          width: 12,
          length: "50%",
          distance: -500,
          offsetCenter: [0, "-15%"],
          itemStyle: {
            color: "#414143",
            shadowBlur: 5,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          distance: -200,
          length: 201,
          lineStyle: {
            color: "#fff",
            width: 5,
          },
        },
        axisLabel: {
          show: false,
        },
        detail: {
          show: true,
          valueAnimation: true,
          formatter: () => "",
          color: "inherit",
          backgroundColor: {
            image: getEmoji(overAllPercentage),
          },
          height: 35,
          width: 25,
          offsetCenter: [0, "32%"],
        },
        title: {
          offsetCenter: [0, '80%'],
          fontSize: 18,
          fontWeight: 'Bold',
          color: '#0e0a65'
        },
        data: [
          {
            value: series,
          },
        ],
      },
    ],
  };

  // Fetch overall score for selected company
  useEffect(() => {
    if (selectedCompanyContainer) {
      const fetchCompanyReport = async () => {
        try {
          setLoading(true);
          const params = { company: selectedCompanyContainer.companyid };
          const response = await fetchOverAllScoreByCompany(params);

          if (response.data.message === "No Records found") {
            setNoData(true);
            setOverAllPercentage(-1);
            setSeries(0);
            return;
          }

          if (response.data?.data?.score) {
            const overallscore = Math.round(response.data.data.score);
            setOverAllPercentage(overallscore);
            setNoData(false);
          }
        } catch (error) {
          console.error("Failed to fetch data:", error);
          setNoData(true);
        } finally {
          setLoading(false);
        }
      };

      fetchCompanyReport();
    }
  }, [selectedCompanyContainer]);

  // Update chart properties based on overall percentage
  useEffect(() => {
    if (overAllPercentage >= 80) {
      setSeries(9);
      setSecurityLevel("Informational");
      setSecurityColor("#2fc430");
    } else if (overAllPercentage >= 60) {
      setSeries(7);
      setSecurityLevel("Low");
      setSecurityColor("#4baeec");
    } else if (overAllPercentage >= 40) {
      setSeries(5);
      setSecurityLevel("Medium");
      setSecurityColor("#4c4ccc");
    } else if (overAllPercentage >= 20) {
      setSeries(3);
      setSecurityLevel("High");
      setSecurityColor("#fc659c");
    } else  if(overAllPercentage>=0){
      setSeries(1);
      setSecurityLevel("Critical");
      setSecurityColor("#eb4b2c");
    }
  }, [overAllPercentage]);

  return (
    <div className="my-md text-center integrated-dashboard-bg" style={{minHeight:noData?'100%':'inherit'}}>
      <div>
        <div className="risk-posture-title">InfoSec Compliance</div>
         {!noData && (<div className="sub-title" style={{ color: securityColor }}>
          {securityLevel}
        </div>
        )}
        <div className="gauge-chart">
          {loading ? (
            <div className="text-center mt-4">
              <CircularProgress size={60} />
            </div>
          ) : noData ? (
            <div className="text-center  v-chart" style={{ fontSize: '18px', color: '#999',minHeight:'90%',marginTop:'150px' }}>
              No Data Found
            </div>
          ) : (
            <EChartsReact
              className='vchart'
              option={gaugeOptions}
              notMerge={true}
              lazyUpdate={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CAPGraph;
